import { addDays, format, parseISO } from "date-fns";
import { CONFIRM_PAYMENT } from "@/constants";

export const DEFAULT_BOOKING_TIME_ZONE = CONFIRM_PAYMENT.env.bookingTimeZone;

const dateTimeFormatterCache = new Map<string, Intl.DateTimeFormat>();
const timeFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getDateTimeFormatter = (timeZone: string) => {
  const key = `datetime:${timeZone}`;

  if (!dateTimeFormatterCache.has(key)) {
    dateTimeFormatterCache.set(
      key,
      new Intl.DateTimeFormat("en-CA", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    );
  }

  return dateTimeFormatterCache.get(key)!;
};

const getTimeFormatter = (timeZone: string) => {
  const key = `time:${timeZone}`;

  if (!timeFormatterCache.has(key)) {
    timeFormatterCache.set(
      key,
      new Intl.DateTimeFormat("en-GB", {
        timeZone,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      }),
    );
  }

  return timeFormatterCache.get(key)!;
};

const getDateTimePartsInTimeZone = (date: Date, timeZone: string) => {
  const parts = getDateTimeFormatter(timeZone).formatToParts(date);

  const getNumericPart = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");

  return {
    year: getNumericPart("year"),
    month: getNumericPart("month"),
    day: getNumericPart("day"),
    hour: getNumericPart("hour"),
    minute: getNumericPart("minute"),
    second: getNumericPart("second"),
  };
};

const toDateString = ({ year, month, day }: { year: number; month: number; day: number }) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export const isValidTimeZone = (timeZone: string) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
};

export const resolveTimeZone = (
  timeZone: string | null | undefined,
  fallback = DEFAULT_BOOKING_TIME_ZONE,
) => {
  const normalized = timeZone?.trim();

  return normalized && isValidTimeZone(normalized) ? normalized : fallback;
};

export const getBrowserTimeZone = (fallback = DEFAULT_BOOKING_TIME_ZONE) =>
  resolveTimeZone(
    typeof window === "undefined" ? fallback : Intl.DateTimeFormat().resolvedOptions().timeZone,
    fallback,
  );

export const getDateStringInTimeZone = (date: Date, timeZone: string) =>
  toDateString(getDateTimePartsInTimeZone(date, timeZone));

export const getTimeZoneOffsetMilliseconds = (date: Date, timeZone: string) => {
  const parts = getDateTimePartsInTimeZone(date, timeZone);
  const utcTimestamp = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  return utcTimestamp - date.getTime();
};

export const zonedDateTimeToUtcDate = (date: string, time: string, timeZone: string) => {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);

  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  let offset = getTimeZoneOffsetMilliseconds(new Date(utcGuess), timeZone);
  let resolved = utcGuess - offset;
  const secondPassOffset = getTimeZoneOffsetMilliseconds(new Date(resolved), timeZone);

  if (secondPassOffset !== offset) {
    offset = secondPassOffset;
    resolved = utcGuess - offset;
  }

  return new Date(resolved);
};

export const formatTimeInTimeZone = (value: Date | string, timeZone: string) =>
  getTimeFormatter(timeZone).format(value instanceof Date ? value : new Date(value));

// 12-hour session clock (e.g. "2:30 PM") pinned to the booking's zone. Formats an
// absolute instant (start_at/end_at) in `timeZone`; resolveTimeZone guarantees a
// valid zone so this never throws and falls back to the default booking zone.
export const formatSessionClock = (value: Date | string, timeZone: string | null | undefined) =>
  new Date(value).toLocaleTimeString("en-US", {
    timeZone: resolveTimeZone(timeZone),
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

export const getUtcDayBoundsForTimeZoneDate = (date: string, timeZone: string) => {
  const start = zonedDateTimeToUtcDate(date, "00:00", timeZone);
  const nextDate = format(addDays(parseISO(date), 1), "yyyy-MM-dd");
  const end = zonedDateTimeToUtcDate(nextDate, "00:00", timeZone);

  return { start, end };
};

export const getDateStringsBetween = (startDate: string, endDate: string) => {
  if (startDate > endDate) {
    return [];
  }

  const dates: string[] = [];
  let current = parseISO(startDate);
  const end = parseISO(endDate);

  while (current <= end) {
    dates.push(format(current, "yyyy-MM-dd"));
    current = addDays(current, 1);
  }

  return dates;
};
